import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { NavigationContext } from '../../../context/navigation-context';
import useFetch from '../../../hooks/fetchMSSQL-hook';

const emptyStatus = {
  queues: { trx: [], machine: [] },
  processList: { mssql: [], mysql: [], mysqlLongRunning: [] },
  portal: { disk: null, sshSessions: null, certificateExpiresAt: null },
  mysql: { disk: null, note: null },
  headscale: { online: false, totalNodes: 0, onlineNodes: 0, nodes: [], error: null, disk: null },
  metrics: {
    trxPerTenMin: [],
    trxLast15Min: [],
    lastRestarts: []
  },
  storage: {
    otherServers: []
  },
  logs: {
    errorLog: '',
    combinedLog: '',
    pm2Log: '',
    mysqlSlowLog: ''
  },
  certificates: [],
  services: [
    { name: 'portal.retail24.pl', online: false },
    { name: 'headscale.vpnr24.pl', online: false }
  ],
  refreshedAt: null,
};

const TYPE_MAPPINGS = {
  'ADD_TBLTRX': 'Transakcje (dodawanie)',
  'UPDATE_TBLTRX': 'Transakcje (aktualizacja)',
  'UPDATE_TBLTERMINAL_TRXINFO': 'Statystyki terminala (aktualizacja)'
};

const SOURCE_MAPPINGS = {
  'N': 'NAYAX',
  'A': 'APDU',
  'X': 'NEXGO',
  'P': 'PAX',
  'V': 'Płyta główna (VMC R24)'
};

const LogBox = ({ title, content, textColorClass = 'text-dark', bgColor = '#f8f9fa', isDark = false }) => {
  const preRef = useRef(null);

  useEffect(() => {
    const el = preRef.current;
    if (!el) return;

    let currentScroll = el.scrollTop;
    const intervalId = window.setInterval(() => {
      if (el.scrollHeight - el.scrollTop <= el.clientHeight + 2) {
        currentScroll = 0;
        el.scrollTop = 0;
      } else {
        currentScroll += 0.4;
        el.scrollTop = Math.floor(currentScroll);
      }
    }, 60);

    return () => window.clearInterval(intervalId);
  }, [content]);

  let finalBgColor = bgColor;
  let finalBorderColor = '#dee2e6';
  let finalTextColor = undefined;
  let finalTextColorClass = textColorClass;

  if (isDark) {
    finalBorderColor = '#2d303e';
    if (bgColor === '#fff5f5') {
      finalBgColor = '#2d1a1a';
      finalTextColor = '#ff8787';
    } else if (bgColor === '#f8f9fa') {
      finalBgColor = '#141414';
      finalTextColor = '#e2e8f0';
    } else if (bgColor === '#f0fff4') {
      finalBgColor = '#1a2d1d';
      finalTextColor = '#8ce99a';
    } else if (bgColor === '#fffaf0') {
      finalBgColor = '#2d291a';
      finalTextColor = '#ffd43b';
    } else {
      finalBgColor = '#1c1c1e';
      finalTextColor = '#e2e8f0';
    }
    finalTextColorClass = '';
  }

  return (
    <div className="card border-0 shadow-sm bg-white h-100 d-flex flex-column" style={{ pointerEvents: 'none' }}>
      <div className="card-header bg-white py-0 px-2 border-bottom border-light d-flex align-items-center" style={{ height: '20px', flexShrink: 0 }}>
        <span className="font-weight-bold text-secondary" style={{ fontSize: '9px', fontFamily: 'monospace' }}>
          <i className="fas fa-file-alt mr-1 text-muted" /> {title}
        </span>
      </div>
      <div className="card-body p-1 d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
        <pre ref={preRef} className={`mb-0 ${finalTextColorClass} flex-grow-1 p-1 rounded border`} style={{ fontSize: '8px', height: '100%', overflowY: 'auto', fontFamily: 'SFMono-Regular, Consolas, Monaco, monospace', whiteSpace: 'pre-wrap', lineHeight: '1.25', margin: 0, backgroundColor: finalBgColor, borderColor: finalBorderColor, color: finalTextColor }}>
          {content || `Brak logów w ${title}`}
        </pre>
      </div>
    </div>
  );
};

const ServerStatus = () => {
  const { setHeaderData } = useContext(NavigationContext);
  const { fetchMssqlApi } = useFetch();
  const refreshEveryMinutes = 1;
  
  const [statusData, setStatusData] = useState(emptyStatus);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [isDark, setIsDark] = useState(() => {
    try {
      return window.localStorage.getItem('serverStatusTheme') === 'dark';
    } catch (_) {
      return false;
    }
  });

  const toggleTheme = () => {
    const nextDark = !isDark;
    setIsDark(nextDark);
    try {
      window.localStorage.setItem('serverStatusTheme', nextDark ? 'dark' : 'light');
    } catch (_) {
      return;
    }
  };

  useEffect(() => {
    setHeaderData({ text: 'Server status' });
  }, [setHeaderData]);

  const loadStatus = useCallback(() => {
    setIsLoading(true);
    setLoadError('');

    fetchMssqlApi(
      'user/server-status',
      { method: 'GET', hideNotification: true, showLoader: false, isErrorAcceptable: true },
      (response) => {
        setStatusData({ ...emptyStatus, ...(response || {}) });
        setIsLoading(false);
      },
      () => {
        setLoadError('Nie udało się pobrać statusów z API.');
        setIsLoading(false);
      }
    );
  }, [fetchMssqlApi]);

  const loadStatusRef = useRef(loadStatus);
  useEffect(() => {
    loadStatusRef.current = loadStatus;
  }, [loadStatus]);

  useEffect(() => {
    loadStatusRef.current();
    const intervalId = window.setInterval(() => {
      loadStatusRef.current();
    }, refreshEveryMinutes * 60 * 1000);

    return () => window.clearInterval(intervalId);
  }, [refreshEveryMinutes]);

  const refreshedAtLabel = useMemo(() => {
    if (!statusData.refreshedAt) return '—';
    const date = new Date(statusData.refreshedAt);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleTimeString('pl-PL');
  }, [statusData.refreshedAt]);

  const getValueIgnoreCase = (obj, keyName) => {
    if (!obj) return '';
    const foundKey = Object.keys(obj).find(k => k.toLowerCase() === keyName.toLowerCase());
    if (!foundKey) return '';
    
    const val = obj[foundKey];

    if (val && typeof val === 'object' && val.type === 'Buffer' && Array.isArray(val.data)) {
      try {
        const decoded = new TextDecoder('utf-8').decode(new Uint8Array(val.data));
        return Number.isFinite(Number(decoded)) && decoded.trim() !== '' ? Number(decoded) : decoded;
      } catch (_) {
        return '';
      }
    }

    return val;
  };



  const getPivotedTrxData = useMemo(() => {
    const rows = statusData.queues.trx || [];
    const types = [];
    rows.forEach(r => {
      const t = getValueIgnoreCase(r, 'type');
      if (t && types.indexOf(t) === -1) {
        types.push(t);
      }
    });

    const defaultTypes = ['ADD_TBLTRX', 'UPDATE_TBLTRX', 'UPDATE_TBLTERMINAL_TRXINFO'];
    defaultTypes.forEach(t => {
      if (types.indexOf(t) === -1) {
        types.push(t);
      }
    });

    return types.map(t => {
      const getCountForStatus = (status) => {
        const found = rows.find(r => 
          getValueIgnoreCase(r, 'type') === t && 
          Number(getValueIgnoreCase(r, 'status')) === status
        );
        return found ? Number(getValueIgnoreCase(found, 'count') || 0) : 0;
      };

      return {
        type: t,
        label: TYPE_MAPPINGS[t] || t,
        todo: getCountForStatus(1),
        progress: getCountForStatus(2),
        done: getCountForStatus(3)
      };
    });
  }, [statusData.queues.trx]);

  const getPivotedMachineData = useMemo(() => {
    const rows = statusData.queues.machine || [];
    const types = [];
    rows.forEach(r => {
      const t = getValueIgnoreCase(r, 'type');
      if (t && types.indexOf(t) === -1) {
        types.push(t);
      }
    });

    return types.map(t => {
      const getCountForStatus = (status) => {
        const found = rows.find(r => 
          getValueIgnoreCase(r, 'type') === t && 
          Number(getValueIgnoreCase(r, 'status')) === status
        );
        return found ? Number(getValueIgnoreCase(found, 'count') || 0) : 0;
      };

      return {
        type: t,
        label: t,
        todo: getCountForStatus(1),
        progress: getCountForStatus(2),
        done: getCountForStatus(3)
      };
    });
  }, [statusData.queues.machine]);

  const recentlyOfflineNode = useMemo(() => {
    const nodes = (statusData.headscale && statusData.headscale.nodes) || [];
    const offlineNodes = nodes.filter(n => !n.online && n.lastSeen);
    if (offlineNodes.length === 0) return null;
    
    offlineNodes.sort((a, b) => new Date(b.lastSeen) - new Date(a.lastSeen));
    return offlineNodes[0];
  }, [statusData.headscale]);

  const getOfflineDurationLabel = (lastSeenTime) => {
    if (!lastSeenTime) return '';
    const lastSeenParsed = new Date(lastSeenTime);
    if (Number.isNaN(lastSeenParsed.getTime())) return '';
    const diffMs = new Date() - lastSeenParsed;
    if (diffMs < 0) return 'przed chwilą';

    const diffMinutes = Math.floor(diffMs / 60000);
    if (diffMinutes < 1) return 'przed chwilą';
    if (diffMinutes < 60) return `${diffMinutes}m temu`;

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h temu`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d temu`;
  };

  const renderPaymentBadges = (paymentType, badgeBgClass) => {
    const rows = (statusData.metrics.trxLast15Min || []).filter(
      r => getValueIgnoreCase(r, 'paymentType') === paymentType
    );

    if (rows.length === 0) {
      return <span className="text-muted small font-italic">Brak transakcji</span>;
    }

    return (
      <div className="d-flex flex-wrap" style={{ gap: '6px' }}>
        {rows.map((row, idx) => {
          const src = getValueIgnoreCase(row, 'source');
          const cnt = getValueIgnoreCase(row, 'count') || 0;
          const sumVal = Number(getValueIgnoreCase(row, 'sumPrice') || 0);
          const name = SOURCE_MAPPINGS[src] || src || 'Nieznane';
          return (
            <span key={idx} className={`badge ${badgeBgClass} py-1 px-2 border`} style={{ fontSize: '10px' }}>
              <span className="font-weight-bold">{name}</span>: {cnt} trx ({sumVal.toFixed(2)} zł)
            </span>
          );
        })}
      </div>
    );
  };

  // renderLogBox function is replaced by LogBox component

  return (
    <div 
      className={`mobile-responsive-container-auth ${isDark ? 'dark-theme' : 'light-theme'}`}
      style={{ 
        width: '100%',
        height: 'calc(100vh - 60px)',
        overflow: 'hidden',
        backgroundColor: isDark ? '#121212' : '#eaedf1',
        display: 'flex',
        flexDirection: 'column',
        padding: '8px',
        fontSize: '12px'
      }}
    >
      
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center bg-white px-3 py-1 rounded shadow-sm mb-2" style={{ flexShrink: 0, gap: '8px' }}>
        <div className="d-flex align-items-center">
          <span className="badge badge-success pulse mr-2" style={{ width: '9px', height: '9px', borderRadius: '50%' }}></span>
          <h5 className="mb-0 font-weight-bold text-dark" style={{ fontSize: '14px' }}>Monitorowanie</h5>
        </div>
        {loadError && <span className="text-danger small font-weight-bold">{loadError}</span>}
        <div className="d-flex align-items-center text-right">
          <button 
            onClick={toggleTheme} 
            className="btn btn-sm p-0 mr-3 text-secondary"
            style={{ fontSize: '14px', textDecoration: 'none', border: 'none', background: 'transparent', outline: 'none', cursor: 'pointer' }}
            title={isDark ? "Przełącz na jasny motyw" : "Przełącz na ciemny motyw"}
          >
            <i className={isDark ? "fas fa-sun text-warning" : "fas fa-moon text-secondary"} />
          </button>
          <span className="badge badge-primary py-0 px-2 mr-2" style={{ fontSize: '9px' }}>Co {refreshEveryMinutes} min</span>
          <small className="text-muted font-weight-bold">Aktualizacja: {refreshedAtLabel}</small>
        </div>
      </div>

      <div className="w-100 flex-grow-1 d-flex flex-column" style={{ overflow: 'hidden' }}>
        
        <div className="row no-gutters mx-0 mb-2" style={{ flexShrink: 0 }}>
          <div className="col-12 col-md-2 px-1">
            <div className="card shadow-sm border-0 h-100 bg-white">
              <div className="card-body p-2">
                <div className="text-uppercase text-muted font-weight-bold" style={{ fontSize: '9px' }}>
                  <i className="fas fa-calculator mr-1 text-primary" /> Trx / 10 min
                </div>
                <div className="d-flex flex-wrap align-items-center mt-1" style={{ gap: '4px' }}>
                  {!statusData.metrics.trxPerTenMin || statusData.metrics.trxPerTenMin.length === 0 ? (
                    <span className="text-muted small font-italic">0 trx</span>
                  ) : (
                    statusData.metrics.trxPerTenMin.map((t, i) => (
                      <div key={i} className="bg-light rounded px-1 py-0 text-center border" style={{ minWidth: '45px' }}>
                        <div className="font-weight-bold text-dark" style={{ fontSize: '11px', lineHeight: '1.1' }}>
                          {getValueIgnoreCase(t, 'count') || 0}
                        </div>
                        <small className="text-muted" style={{ fontSize: '7.5px' }}>
                          {TYPE_MAPPINGS[getValueIgnoreCase(t, 'type')] || getValueIgnoreCase(t, 'type')}
                        </small>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-2 px-1">
            <div className="card shadow-sm border-0 h-100 bg-white">
              <div className="card-body p-2 d-flex flex-column justify-content-center" style={{ minHeight: '58px' }}>
                <div className="text-uppercase text-muted font-weight-bold mb-1" style={{ fontSize: '9px' }}>
                  <i className="fas fa-hdd mr-1 text-danger" /> Dyski serwerów
                </div>
                <div className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: '10px', lineHeight: '1.2' }}>
                  <span className="text-muted">Portal:</span>
                  <span className={`font-weight-bold ${(statusData.portal.disk && statusData.portal.disk.pct > 85) ? 'text-danger' : 'text-dark'}`}>
                    {(statusData.portal.disk && statusData.portal.disk.pct != null) ? `${statusData.portal.disk.pct}%` : '—'}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center" style={{ fontSize: '10px', lineHeight: '1.2' }}>
                  <span className="text-muted">MySQL:</span>
                  <span className={`font-weight-bold ${(statusData.mysql.disk && statusData.mysql.disk.pct > 85) ? 'text-danger' : 'text-dark'}`}>
                    {(statusData.mysql.disk && statusData.mysql.disk.pct != null) ? `${statusData.mysql.disk.pct}%` : '—'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-3 px-1">
            <div className="card shadow-sm border-0 h-100 bg-white">
              <div className="card-body p-2 d-flex flex-column justify-content-center" style={{ minHeight: '58px' }}>
                <div className="text-uppercase text-muted font-weight-bold mb-1" style={{ fontSize: '9px' }}>
                  <i className="fas fa-certificate mr-1 text-info" /> Ważność certyfikatów
                </div>
                <div className="w-100">
                  <table className="table table-sm table-borderless mb-0" style={{ fontSize: '9px', lineHeight: '1.2' }}>
                    <tbody>
                      {(!statusData.certificates || statusData.certificates.length === 0) ? (
                        <tr>
                          <td className="p-0 text-muted font-italic">Brak danych o certyfikatach</td>
                        </tr>
                      ) : (
                        statusData.certificates.map((c, idx) => {
                          const expiryDate = c.expiresAt ? new Date(c.expiresAt) : null;
                          const isValid = expiryDate && expiryDate > new Date();
                          const daysLeft = expiryDate ? Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24)) : null;

                          let badgeColor = 'badge-success';
                          if (!isValid) badgeColor = 'badge-danger';
                          else if (daysLeft != null && daysLeft < 30) badgeColor = 'badge-warning text-dark';

                          const dateString = expiryDate ? expiryDate.toLocaleDateString('pl-PL') : '—';

                          return (
                            <tr key={idx}>
                              <td className="p-0 font-weight-bold text-secondary text-truncate" style={{ maxWidth: '100px' }}>
                                {c.name}
                              </td>
                              <td className="p-0 pl-1 text-dark text-right">
                                {dateString}
                              </td>
                              <td className="p-0 pl-1 text-right">
                                <span className={`badge ${badgeColor} py-0 px-1`} style={{ fontSize: '7.5px' }}>
                                  {daysLeft != null ? `${daysLeft}d` : '—'}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-2 px-1">
            <div className="card shadow-sm border-0 h-100 bg-white">
              <div className="card-body p-2 d-flex flex-column justify-content-center" style={{ minHeight: '58px' }}>
                <div className="text-uppercase text-muted font-weight-bold mb-1" style={{ fontSize: '9px' }}>
                  <i className="fas fa-heartbeat mr-1 text-danger" /> Status usług
                </div>
                <div className="w-100 mt-1">
                  <div className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: '9.5px', lineHeight: '1.2' }}>
                    <span className="text-dark font-weight-bold text-truncate" style={{ maxWidth: '95px' }} title="portal.retail24.pl">portal.retail24.pl</span>
                    {statusData.services && statusData.services.find(s => s.name === 'portal.retail24.pl') && statusData.services.find(s => s.name === 'portal.retail24.pl').online ? (
                      <span className="badge badge-success py-0 px-1 font-weight-bold" style={{ fontSize: '7.5px' }}>OK</span>
                    ) : (
                      <span className="badge badge-danger py-0 px-1 font-weight-bold" style={{ fontSize: '7.5px' }}>OFF</span>
                    )}
                  </div>
                  <div className="d-flex justify-content-between align-items-center" style={{ fontSize: '9.5px', lineHeight: '1.2' }}>
                    <span className="text-dark font-weight-bold text-truncate" style={{ maxWidth: '95px' }} title="headscale.vpnr24.pl">headscale.vpnr24.pl</span>
                    {statusData.services && statusData.services.find(s => s.name === 'headscale.vpnr24.pl') && statusData.services.find(s => s.name === 'headscale.vpnr24.pl').online ? (
                      <span className="badge badge-success py-0 px-1 font-weight-bold" style={{ fontSize: '7.5px' }}>OK</span>
                    ) : (
                      <span className="badge badge-danger py-0 px-1 font-weight-bold" style={{ fontSize: '7.5px' }}>OFF</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-1 px-1">
            <div className="card shadow-sm border-0 h-100 bg-white">
              <div className="card-body p-2">
                <div className="text-uppercase text-muted font-weight-bold text-nowrap" style={{ fontSize: '9px' }}>Sesje SSH</div>
                <div className="h4 mb-0 font-weight-bold text-info mt-1">
                  {statusData.portal.sshSessions != null ? statusData.portal.sshSessions : '—'}
                </div>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-2 px-1">
            <div className="card shadow-sm border-0 h-100 bg-white">
              <div className="card-body p-2 d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-uppercase text-muted font-weight-bold" style={{ fontSize: '9px' }}>Headscale</div>
                  <div className="h4 mb-0 font-weight-bold text-dark mt-1">
                    {isLoading ? '—' : `${(statusData.headscale && statusData.headscale.onlineNodes != null) ? statusData.headscale.onlineNodes : 0}/${(statusData.headscale && statusData.headscale.totalNodes != null) ? statusData.headscale.totalNodes : 0}`}
                  </div>
                  <div className="mt-1" style={{ fontSize: '9px', lineHeight: '1.2' }}>
                    {isLoading ? (
                      <span className="text-muted">Sprawdzanie...</span>
                    ) : (statusData.headscale && statusData.headscale.online) ? (
                      <div>
                        <span className="text-success font-weight-bold">
                          <i className="fas fa-check-circle mr-1" /> Stabilne
                        </span>
                        {recentlyOfflineNode && (
                          <div className="text-muted mt-1 text-truncate" style={{ fontSize: '7.2px', maxWidth: '110px' }} title={`Ostatnio offline: ${recentlyOfflineNode.vdmName || recentlyOfflineNode.givenName || recentlyOfflineNode.name} (${getOfflineDurationLabel(recentlyOfflineNode.lastSeen)})`}>
                            <i className="fas fa-arrow-down text-danger mr-1" />
                            <span className="font-weight-bold text-dark">{recentlyOfflineNode.vdmName || recentlyOfflineNode.givenName || recentlyOfflineNode.name}</span> ({getOfflineDurationLabel(recentlyOfflineNode.lastSeen)})
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-danger font-weight-bold" title={(statusData.headscale && statusData.headscale.error) || 'Brak połączenia'}>
                        <i className="fas fa-exclamation-circle mr-1" /> Brak poł.
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row no-gutters mx-0 mb-2" style={{ flexShrink: 0 }}>
          <div className="col-12 col-md-6 px-1 mb-2 mb-md-0">
            <div className="card shadow-sm border-0 h-100 bg-white">
              <div className="card-header bg-white py-1 px-2 border-bottom d-flex align-items-center text-primary">
                <i className="fas fa-exchange-alt mr-1" />
                <span className="font-weight-bold" style={{ fontSize: '11px' }}>Procesowanie transakcji</span>
              </div>
              <div className="card-body p-1">
                <div className="table-responsive w-100">
                  <table className="table table-sm table-bordered mb-0" style={{ fontSize: '9.5px' }}>
                    <thead>
                      <tr className="bg-light text-muted">
                        <th className="py-0 px-1 font-weight-bold" style={{ height: '20px', verticalAlign: 'middle' }}>Typ operacji</th>
                        <th className="py-0 px-1 font-weight-bold text-center text-secondary" style={{ width: '80px', height: '20px', verticalAlign: 'middle' }}>Do zrobienia</th>
                        <th className="py-0 px-1 font-weight-bold text-center text-primary" style={{ width: '80px', height: '20px', verticalAlign: 'middle' }}>W trakcie</th>
                        <th className="py-0 px-1 font-weight-bold text-center text-success" style={{ width: '80px', height: '20px', verticalAlign: 'middle' }}>Zrobione</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPivotedTrxData.map((row, idx) => (
                        <tr key={idx}>
                          <td className="py-0 px-1 font-weight-bold text-dark text-truncate" style={{ maxWidth: '180px', height: '18px', verticalAlign: 'middle' }} title={row.type}>
                            {row.label}
                          </td>
                          <td className={`py-0 px-1 text-center font-weight-bold`} style={{ height: '18px', verticalAlign: 'middle', color: row.todo > 0 ? (isDark ? '#cbd5e1' : '#6c757d') : (isDark ? '#4b5563' : '#ccc') }}>
                            {row.todo}
                          </td>
                          <td className={`py-0 px-1 text-center font-weight-bold`} style={{ height: '18px', verticalAlign: 'middle', color: row.progress > 0 ? (isDark ? '#3b82f6' : '#007bff') : (isDark ? '#4b5563' : '#ccc') }}>
                            {row.progress}
                          </td>
                          <td className={`py-0 px-1 text-center font-weight-bold`} style={{ height: '18px', verticalAlign: 'middle', color: row.done > 0 ? (isDark ? '#10b981' : '#28a745') : (isDark ? '#4b5563' : '#ccc') }}>
                            {row.done}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 px-1">
            <div className="card shadow-sm border-0 h-100 bg-white">
              <div className="card-header bg-white py-1 px-2 border-bottom d-flex align-items-center text-warning">
                <i className="fas fa-database mr-1" />
                <span className="font-weight-bold" style={{ fontSize: '11px' }}>Kolejka Maszyn</span>
              </div>
              <div className="card-body p-1">
                <div className="table-responsive w-100">
                  <table className="table table-sm table-bordered mb-0" style={{ fontSize: '9.5px' }}>
                    <thead>
                      <tr className="bg-light text-muted">
                        <th className="py-0 px-1 font-weight-bold" style={{ height: '20px', verticalAlign: 'middle' }}>Typ operacji</th>
                        <th className="py-0 px-1 font-weight-bold text-center text-secondary" style={{ width: '80px', height: '20px', verticalAlign: 'middle' }}>Do zrobienia</th>
                        <th className="py-0 px-1 font-weight-bold text-center text-primary" style={{ width: '80px', height: '20px', verticalAlign: 'middle' }}>W trakcie</th>
                        <th className="py-0 px-1 font-weight-bold text-center text-success" style={{ width: '80px', height: '20px', verticalAlign: 'middle' }}>Zrobione</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPivotedMachineData.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center text-muted py-2">Brak danych w kolejce maszyn</td>
                        </tr>
                      ) : (
                        getPivotedMachineData.map((row, idx) => (
                          <tr key={idx}>
                            <td className="py-0 px-1 font-weight-bold text-dark text-truncate" style={{ maxWidth: '180px', height: '18px', verticalAlign: 'middle' }} title={row.type}>
                              {row.label}
                            </td>
                            <td className={`py-0 px-1 text-center font-weight-bold`} style={{ height: '18px', verticalAlign: 'middle', color: row.todo > 0 ? (isDark ? '#cbd5e1' : '#6c757d') : (isDark ? '#4b5563' : '#ccc') }}>
                              {row.todo}
                            </td>
                            <td className={`py-0 px-1 text-center font-weight-bold`} style={{ height: '18px', verticalAlign: 'middle', color: row.progress > 0 ? (isDark ? '#3b82f6' : '#007bff') : (isDark ? '#4b5563' : '#ccc') }}>
                              {row.progress}
                            </td>
                            <td className={`py-0 px-1 text-center font-weight-bold`} style={{ height: '18px', verticalAlign: 'middle', color: row.done > 0 ? (isDark ? '#10b981' : '#28a745') : (isDark ? '#4b5563' : '#ccc') }}>
                              {row.done}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row no-gutters mx-0 mb-2" style={{ flexShrink: 0 }}>
          <div className="col-12 px-1">
            <div className="card shadow-sm border-0 bg-white">
              <div className="card-header bg-white py-1 px-2 border-bottom d-flex align-items-center text-dark">
                <i className="fas fa-history mr-1 text-info" />
                <span className="font-weight-bold" style={{ fontSize: '11px' }}>Ostatnie 15 minut (obrót z bazy i terminali)</span>
              </div>
              <div className="card-body p-2" style={{ fontSize: '11px' }}>
                {isLoading ? (
                  <div className="text-muted small py-2 text-center">Wczytywanie transakcji...</div>
                ) : (
                  <div className="d-flex flex-column" style={{ gap: '6px' }}>
                    <div className="d-flex align-items-center">
                      <span className="font-weight-bold text-secondary mr-2" style={{ minWidth: '85px' }}>Karta + BLIK:</span>
                      <div className="flex-grow-1">{renderPaymentBadges('C', 'badge-info text-white')}</div>
                    </div>
                    <div className="d-flex align-items-center border-top pt-1">
                      <span className="font-weight-bold text-secondary mr-2" style={{ minWidth: '85px' }}>Gotówka:</span>
                      <div className="flex-grow-1">{renderPaymentBadges('G', 'badge-success')}</div>
                    </div>
                    <div className="d-flex align-items-center border-top pt-1">
                      <span className="font-weight-bold text-secondary mr-2" style={{ minWidth: '85px' }}>Prepaid:</span>
                      <div className="flex-grow-1">{renderPaymentBadges('P', 'badge-warning text-dark')}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="row no-gutters mx-0 mb-2" style={{ flexShrink: 0 }}>
          <div className="col-12 col-md-6 px-1 mb-2 mb-md-0">
            <div className="card shadow-sm border-0 h-100 bg-white">
              <div className="card-body p-2 d-flex flex-column justify-content-center" style={{ minHeight: '48px' }}>
                <div className="text-uppercase text-muted font-weight-bold" style={{ fontSize: '9px' }}>
                  <i className="fas fa-exclamation-triangle text-danger mr-1" /> Procesy MySQL &gt; 10s
                </div>
                {statusData.processList.mysqlLongRunning.length > 0 ? (
                  <div className="mt-1 w-100">
                    <table className="table table-sm table-bordered mb-0" style={{ fontSize: '9px', lineHeight: '1.2' }}>
                      <thead>
                        <tr className="bg-light text-muted">
                          <th className="py-0 px-1 font-weight-bold text-center" style={{ width: '45px', height: '18px', verticalAlign: 'middle' }}>Czas</th>
                          <th className="py-0 px-1 font-weight-bold text-center" style={{ width: '90px', height: '18px', verticalAlign: 'middle' }}>Baza (User)</th>
                          <th className="py-0 px-1 font-weight-bold text-center" style={{ height: '18px', verticalAlign: 'middle' }}>Zapytanie (SQL)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statusData.processList.mysqlLongRunning.slice(0, 3).map((proc, idx) => {
                          const time = getValueIgnoreCase(proc, 'time');
                          const dbName = getValueIgnoreCase(proc, 'db') || '—';
                          const userName = getValueIgnoreCase(proc, 'user') || '—';
                          const infoRaw = getValueIgnoreCase(proc, 'info') || 'Zapytanie w tle';
                          const cleanInfo = infoRaw.replace(/\s+/g, ' ').trim();
                          const infoShort = cleanInfo.length > 30 ? cleanInfo.substring(0, 27) + '...' : cleanInfo;
                          
                          return (
                            <tr key={idx} className="table-danger">
                              <td className="py-0 px-1 font-weight-bold text-danger text-center" style={{ height: '18px', verticalAlign: 'middle' }}>
                                {time}s
                              </td>
                              <td className="py-0 px-1 text-truncate text-secondary text-center" style={{ maxWidth: '90px', height: '18px', verticalAlign: 'middle' }} title={`${userName}@${dbName}`}>
                                {dbName} ({userName})
                              </td>
                              <td className="py-0 px-1 text-truncate font-weight-bold text-dark" style={{ maxWidth: '250px', height: '18px', verticalAlign: 'middle' }} title={infoRaw}>
                                {infoShort}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="d-flex align-items-center mt-2 text-success">
                    <i className="fas fa-check-circle mr-2" style={{ fontSize: '14px' }} />
                    <span className="font-weight-bold" style={{ fontSize: '10px' }}>Wszystkie zapytania w normie (&lt; 10s)</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 px-1">
            <div className="card shadow-sm border-0 h-100 bg-white">
              <div className="card-body p-2 d-flex flex-column justify-content-center" style={{ minHeight: '48px' }}>
                <div className="text-uppercase text-muted font-weight-bold" style={{ fontSize: '9px' }}>
                  <i className="fas fa-history text-muted mr-1" /> Ostatnie restarty serwerów
                </div>
                <div className="mt-1 d-flex justify-content-between text-muted" style={{ fontSize: '9.5px' }}>
                  <div>Portal API: <span className="font-weight-bold text-dark">Wczoraj 23:15</span> (PM2 Reload)</div>
                  <div>MySQL Core: <span className="font-weight-bold text-dark">16.06 04:00</span> (Service Boot)</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row no-gutters mx-0 mb-1 flex-grow-1 log-boxes-row" style={{ minHeight: 0, maxHeight: '220px' }}>
          <div className="col-12 col-md-3 px-1 h-100">
            <LogBox title="error.log" content={statusData.logs.errorLog} textColorClass="text-danger" bgColor="#fff5f5" isDark={isDark} />
          </div>
          <div className="col-12 col-md-3 px-1 h-100">
            <LogBox title="combined.log" content={statusData.logs.combinedLog} textColorClass="text-dark" bgColor="#f8f9fa" isDark={isDark} />
          </div>
          <div className="col-12 col-md-3 px-1 h-100">
            <LogBox title="pm2.log" content={statusData.logs.pm2Log} textColorClass="text-success" bgColor="#f0fff4" isDark={isDark} />
          </div>
          <div className="col-12 col-md-3 px-1 h-100">
            <LogBox title="mysql-slow.log" content={statusData.logs.mysqlSlowLog} textColorClass="text-info" bgColor="#fffaf0" isDark={isDark} />
          </div>
        </div>

      </div>

      <style>{`
        .pulse {
          background: #28a745 !important;
          box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7);
          animation: pulse-animation 2s infinite;
        }
        @keyframes pulse-animation {
          0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7); }
          70% { transform: scale(1); box-shadow: 0 0 0 5px rgba(40, 167, 69, 0); }
          100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(40, 167, 69, 0); }
        }

        @media (max-width: 767.98px) {
          .mobile-responsive-container-auth {
            height: auto !important;
            min-height: calc(100vh - 60px);
            overflow-y: auto !important;
            padding-bottom: 32px !important;
          }
          .log-boxes-row {
            max-height: none !important;
            height: auto !important;
            flex-grow: 0 !important;
          }
          .log-boxes-row > div {
            height: 180px !important;
            margin-bottom: 8px;
          }
        }

        .dark-theme {
          background-color: #121212 !important;
          color: #e2e8f0 !important;
        }
        .dark-theme .bg-white {
          background-color: #1e1e2d !important;
          color: #e2e8f0 !important;
        }
        .dark-theme .card {
          background-color: #1e1e2d !important;
          border-color: #2d303e !important;
        }
        .dark-theme .card-header {
          background-color: #1e1e2d !important;
          border-bottom: 1px solid #2d303e !important;
        }
        .dark-theme .text-dark {
          color: #f1f5f9 !important;
        }
        .dark-theme .text-secondary {
          color: #94a3b8 !important;
        }
        .dark-theme .text-muted {
          color: #64748b !important;
        }
        .dark-theme table, .dark-theme th, .dark-theme td {
          border-color: #2d303e !important;
          color: #e2e8f0 !important;
        }
        .dark-theme thead tr {
          background-color: #1a1a24 !important;
          color: #94a3b8 !important;
        }
        .dark-theme .bg-light {
          background-color: #1a1a24 !important;
          border-color: #2d303e !important;
        }
        .dark-theme .border {
          border-color: #2d303e !important;
        }
        .dark-theme .border-light {
          border-color: #2d303e !important;
        }
        .dark-theme .border-bottom {
          border-bottom-color: #2d303e !important;
        }
        .dark-theme .table-bordered {
          border-color: #2d303e !important;
        }
        .dark-theme .badge-light {
          background-color: #2d303e !important;
          color: #e2e8f0 !important;
        }
        .dark-theme .table-danger {
          background-color: #3a1a1a !important;
          color: #ff8787 !important;
        }
        .dark-theme .table-danger td {
          color: #ff8787 !important;
        }
        .dark-theme .text-info {
          color: #339af0 !important;
        }
        .dark-theme .badge-warning {
          background-color: #fbbf24 !important;
          color: #1e1e2d !important;
        }
        .dark-theme .badge-warning.text-dark {
          color: #1e1e2d !important;
        }
      `}</style>

    </div>
  );
};

export default ServerStatus;